/**
 * Day Planning & Workorder Constants
 *
 * Centralized configuration for the workorders/day planning feature.
 * All magic numbers and hardcoded values should be defined here.
 */

// =============================================================================
// Time Slots Configuration
// =============================================================================

/**
 * Start hour for time slot selection (24h format)
 * Used in WorkorderCard time selector
 */
export const TIME_SLOT_START_HOUR = 6;

/**
 * End hour for time slot selection (24h format)
 * Used in WorkorderCard time selector
 */
export const TIME_SLOT_END_HOUR = 18;

/**
 * Interval between time slots in minutes
 * Used to generate time slot options (e.g., 6:00, 6:30, 7:00, etc.)
 */
export const TIME_SLOT_INTERVAL_MINUTES = 30;

/**
 * Number of time slots to generate
 * Calculation: ((END - START) * 60 / INTERVAL) + 1
 * For 6:00-18:00 with 30min intervals = 25 slots
 */
export const TIME_SLOT_COUNT = 25;

// =============================================================================
// Map Configuration
// =============================================================================

/**
 * Map display configuration for different contexts
 */
export const MAP_CONFIG = {
  /** Default zoom level for single workorder */
  defaultZoom: 13,
  /** Zoom level for route overview */
  routeZoom: 11,
  /** Map height on mobile (in Tailwind arbitrary values) */
  mobileHeight: 'h-64',
  /** Map height on desktop (in Tailwind arbitrary values) */
  desktopHeight: '@lg:h-[calc(100vh-20rem)]',
  /** Minimum height for map containers */
  minHeight: '@lg:min-h-128',
  /** Default center coordinates for Netherlands (lat, lng) */
  defaultCenter: [52.0, 4.5] as [number, number],
  /** Padding for map bounds (pixels) */
  boundsPadding: [20, 20] as [number, number],
  /** Padding for single coordinate (degrees) */
  singleCoordPadding: 0.02,
  /** Fallback bounds padding (degrees) */
  fallbackPadding: 0.1,
} as const;

// =============================================================================
// HQ Location
// =============================================================================

/**
 * Headquarters location configuration
 * Used as the starting/ending point for all routes
 */
export const HQ_LOCATION = {
  address: 'ABC Westland 555 Poeldijk',
  coords: [52.03351, 4.23749] as [number, number], // [lat, lng]
} as const;

// =============================================================================
// API Request Configuration
// =============================================================================

/**
 * Network request timeouts and limits
 */
export const API_CONFIG = {
  /** Timeout for route calculation requests (milliseconds) */
  routeTimeout: 30000, // 30 seconds
  /** Timeout for geocoding requests (milliseconds) */
  geocodeTimeout: 30000, // 30 seconds
  /** Maximum routes per batch request */
  maxRoutesPerRequest: 100,
  /** Maximum addresses per geocode request */
  maxAddressesPerRequest: 50,
} as const;

// =============================================================================
// Query Configuration (React Query staleTime)
// =============================================================================

/**
 * Cache and stale time configuration for queries
 * All values in milliseconds
 */
export const QUERY_CONFIG = {
  /** Workorders for a specific day */
  dayWorkordersStale: 5 * 60 * 1000, // 5 minutes
  /** All technicians' workorders */
  allTechniciansStale: 5 * 60 * 1000, // 5 minutes
  /** Calculated routes */
  routesStale: 30 * 60 * 1000, // 30 minutes
  /** Geocoded addresses */
  geocodeStale: 60 * 60 * 1000, // 1 hour
  /** Plangroups (teams) */
  plangroupsStale: 5 * 60 * 1000, // 5 minutes
} as const;

// =============================================================================
// UI Constants
// =============================================================================

/**
 * User interface styling and behavior constants
 */
export const UI_CONFIG = {
  /** Spinner/loader animation duration (for testing, not CSS) */
  loaderAnimationDuration: 1000,
  /** Number of skeleton loaders to show while loading */
  skeletonCount: 3,
  /** Debounce delay for search/filter inputs (milliseconds) */
  debounceDelay: 300,
  /** Toast notification duration (milliseconds) */
  toastDuration: 5000,
} as const;

// =============================================================================
// Marker & Icon Sizes
// =============================================================================

/**
 * Map marker dimensions and styling
 */
export const MARKER_CONFIG = {
  /** Standard workorder marker size (pixels) */
  standardSize: 24,
  /** Selected/highlighted workorder marker size (pixels) */
  highlightedSize: 32,
  /** Small marker size (for clustered views) */
  smallSize: 16,
  /** HQ marker size (pixels) */
  hqSize: 28,
  /** Completion badge size (pixels) */
  completionBadgeSize: 14,
  /** Marker font size (pixels) */
  fontSize: 12,
  /** Highlighted marker font size (pixels) */
  highlightedFontSize: 14,
  /** Border width for markers (pixels) */
  borderWidth: 2,
  /** Highlighted marker border width (pixels) */
  highlightedBorderWidth: 3,
} as const;

// =============================================================================
// Coordinate Validation
// =============================================================================

/**
 * Geographic coordinate bounds for validation
 */
export const COORDINATE_BOUNDS = {
  /** Minimum valid latitude */
  minLatitude: -90,
  /** Maximum valid latitude */
  maxLatitude: 90,
  /** Minimum valid longitude */
  minLongitude: -180,
  /** Maximum valid longitude */
  maxLongitude: 180,
} as const;

// =============================================================================
// HTTP Status Codes
// =============================================================================

/**
 * HTTP status codes used in error handling
 */
export const HTTP_STATUS = {
  /** Forbidden - permission denied */
  forbidden: 403,
  /** Too many requests - rate limit */
  tooManyRequests: 429,
} as const;

// =============================================================================
// localStorage Keys
// =============================================================================

/**
 * Keys for localStorage persistence
 */
export const STORAGE_KEYS = {
  /** Selected plangroup IDs */
  selectedPlangroups: 'dayplanning.selectedPlangroups',
  /** Map visibility toggle */
  showMap: 'dayplanning.showMap',
} as const;

// =============================================================================
// Animation Configuration
// =============================================================================

/**
 * Animation settings for route polylines and UI elements
 */
export const ANIMATION_CONFIG = {
  /** Dash array for animated route polylines */
  routeDashArray: '10, 10',
  /** Opacity for dragging elements */
  draggingOpacity: 0.5,
  /** Opacity for completed workorders */
  completedOpacity: 0.7,
} as const;

// =============================================================================
// Time Conversion
// =============================================================================

/**
 * Time conversion constants
 */
export const TIME_CONVERSION = {
  /** Milliseconds per second */
  msPerSecond: 1000,
  /** Seconds per minute */
  secondsPerMinute: 60,
  /** Minutes per hour */
  minutesPerHour: 60,
  /** Hours per day */
  hoursPerDay: 24,
} as const;
